import { graphqlFetch } from '@/utils/graphqlFetch';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Faq,
  FaqCategory,
  FaqCategoryInput,
  FaqInput,
  MutationUpdateFaqArgs,
} from '../gql/graphql';
import {
  CREATE_FAQ,
  CREATE_FAQ_CATEGORY,
  DELETE_FAQ,
  UPDATE_FAQ,
} from '../mutations/faq';
import { FIND_ALL_FAQ_CATEGORIES, FIND_FAQ_BY_CATEGORY } from '../queries/faq';

export const useGetAllFaqCategories = () => {
  return useQuery<FaqCategory[], Error>({
    queryKey: ['faq-categories'],
    queryFn: async () => {
      const data = await graphqlFetch<
        { findAllFaqCategories: FaqCategory[] },
        { query: FaqCategory }
      >(FIND_ALL_FAQ_CATEGORIES);
      return data.findAllFaqCategories;
    },
  });
};

export const useGetFaqByCategory = (category: string) => {
  return useQuery<Faq[], Error>({
    queryKey: ['faq-by-category', category],
    queryFn: async () => {
      const data = await graphqlFetch<
        { findFaqByCategory: Faq[] },
        { category: string }
      >(FIND_FAQ_BY_CATEGORY, { category });
      return data.findFaqByCategory;
    },
    enabled: !!category,
  });
};

export const useCreateFaqCategory = () => {
  const queryClient = useQueryClient();
  return useMutation<FaqCategory, Error, FaqCategoryInput>({
    mutationFn: async (input: FaqCategoryInput) => {
      const data = await graphqlFetch<
        { createFaqCategory: FaqCategory },
        { input: FaqCategoryInput }
      >(CREATE_FAQ_CATEGORY, { input });
      return data.createFaqCategory;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faq-categories'] });
    },
  });
};

export const useCreateFaq = () => {
  const queryClient = useQueryClient();
  return useMutation<Faq, Error, FaqInput>({
    mutationFn: async (input: FaqInput) => {
      const data = await graphqlFetch<{ createFaq: Faq }, { input: FaqInput }>(
        CREATE_FAQ,
        { input },
      );
      return data.createFaq;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faq-by-category'] });
    },
  });
};

export const useUpdateFaq = () => {
  const queryClient = useQueryClient();
  return useMutation<Faq, Error, MutationUpdateFaqArgs>({
    mutationFn: async (input: MutationUpdateFaqArgs) => {
      const data = await graphqlFetch<
        { updateFaq: Faq },
        MutationUpdateFaqArgs
      >(UPDATE_FAQ, { faq: input.faq, input: input.input });
      return data.updateFaq;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faq-by-category'] });
    },
  });
};

export const useDeleteFaq = () => {
  const queryClient = useQueryClient();
  return useMutation<Faq, Error, string>({
    mutationFn: async (faqId: string) => {
      const data = await graphqlFetch<{ deleteFaq: Faq }, { faqId: string }>(
        DELETE_FAQ,
        { faqId },
      );
      return data.deleteFaq;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faq-by-category'] });
    },
  });
};
